<template>
  <CoreDrawer
    :value="value"
    title="Фильтры"
    :width="376"
    data-qa="analyses_lpu_cards_filters_drawer"
    @input="emit('input', $event)"
  >
    <div class="analyses-lpu-filters-drawer d-flex flex-column">
      <LpuCardsFiltersNavItem
        v-for="navItem in navItems"
        :key="navItem.qa"
        :item-data-qa="navItem.qa"
        :icon="navItem.icon"
        :title="navItem.title"
        :subtitle="navItem.subtitle"
        @click="navItem.onClick"
      />

      <v-divider class="my-2" />

      <LpuCardsFiltersToggleItem
        v-for="toggleItem in toggleItems"
        :key="toggleItem.qa"
        :item-data-qa="toggleItem.qa"
        :icon="toggleItem.icon"
        :title="toggleItem.title"
        :value="toggleItem.value"
        :readonly="toggleItem.readonly"
        @input="toggleItem.onInput && toggleItem.onInput($event)"
      />
    </div>

    <template #overlay>
      <v-overlay
        :value="isSortSheetOpen"
        absolute
        @click="emit('close-sort-sheet')"
      />
    </template>

    <template #footer>
      <div class="d-flex flex-column ga-2 pa-2 elevation-8 w-100">
        <v-btn
          block
          depressed
          color="primary"
          :data-qa="primaryFooterButton.qa"
          :disabled="primaryFooterButton.disabled"
          :loading="primaryFooterButton.loading"
          :class="{ 'bg-primary primary--text': primaryFooterButton.isClose }"
          @click="primaryFooterButton.onClick?.()"
        >
          {{ primaryFooterButton.label }}
        </v-btn>
        <v-btn
          v-if="showResetFiltersButton"
          block
          text
          depressed
          class="text--text"
          data-qa="analyses_lpu_cards_filters_reset_button"
          @click="emit('reset-filters')"
        >
          <v-icon
            left
            size="24"
          >
            ui-icon-reset
          </v-icon>
          Очистить фильтры
        </v-btn>
      </div>
    </template>
  </CoreDrawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CoreDrawer from 'src/core/CoreDrawer/CoreDrawer.vue'
import useContextStore from 'src/store/useContextStore'
import { getAnalysesLpuSortLabel } from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-sort'
import { formatAnalysesLpuCountLabel } from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/format-analyses-lpu-count-label'
import { getAnalysesPlaceFilterLabel } from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/get-analyses-place-filter-label'
import {
  ANALYSES_LPU_DISCOUNT_CHIP_LABEL,
  ANALYSES_LPU_ONLINE_ORDER_CHIP_LABEL,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-rating-filter'
import LpuCardsFiltersNavItem
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsFiltersNavItem.vue'
import LpuCardsFiltersToggleItem
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsFiltersToggleItem.vue'
import type { AnalysesLpuCardsFilters } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuCardsFilter.types'
import type { AnalysesPageContext } from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'
import type { CommonContextStore } from 'src/types/context'

type NavItem = {
  qa: string
  icon: string
  title: string
  subtitle?: string
  onClick: () => void
}

type ToggleItem = {
  qa: string
  icon: string
  title: string
  value: boolean
  readonly: boolean
  onInput?: (_value: boolean) => void
}

type PrimaryFooterButton = {
  qa: string
  label: string
  disabled?: boolean
  loading?: boolean
  isClose?: boolean
  onClick?: () => void
}

const props = defineProps<{
  value: boolean
  draftFilters: AnalysesLpuCardsFilters
  hasUnsavedFilters: boolean
  showResetFiltersButton: boolean
  isSortSheetOpen?: boolean
  filtersCount?: number
  countLoading?: boolean
  applyLoading?: boolean
}>()

type Emits = {
  (_event: 'input', _value: boolean): void
  (_event: 'open-sort-sheet'): void
  (_event: 'close-sort-sheet'): void
  (_event: 'open-place-drawer'): void
  (_event: 'update:draft-filters', _value: Partial<AnalysesLpuCardsFilters>): void
  (_event: 'apply'): void
  (_event: 'reset-filters'): void
}

const emit = defineEmits<Emits>()

const { context } = useContextStore() as CommonContextStore & {
  context: AnalysesPageContext
}

const sortLabel = computed(() => getAnalysesLpuSortLabel(props.draftFilters.sort))
const placeFilterLabel = computed(() => getAnalysesPlaceFilterLabel(Boolean(context.town.has_metro)))

const placeTitle = computed(() => props.draftFilters.place?.name || placeFilterLabel.value)
const placeSubtitle = computed(() => (
  props.draftFilters.place ? placeFilterLabel.value : undefined
))

const isCountEmpty = computed(() => !props.countLoading && props.filtersCount === 0)

const applyButtonLabel = computed(() => (
  props.filtersCount ? formatAnalysesLpuCountLabel(props.filtersCount) : ''
))

const primaryFooterButton = computed<PrimaryFooterButton>(() => {
  if (props.hasUnsavedFilters) {
    if (isCountEmpty.value) {
      return {
        qa: 'analyses_lpu_cards_filters_not_found_button',
        label: 'Не найдено',
        disabled: true,
      }
    }

    return {
      qa: 'analyses_lpu_cards_filters_apply_button',
      label: applyButtonLabel.value,
      disabled: !props.filtersCount,
      loading: Boolean(props.applyLoading || props.countLoading),
      onClick: () => emit('apply'),
    }
  }

  return {
    qa: 'analyses_lpu_cards_filters_close_button',
    label: 'Закрыть',
    isClose: true,
    onClick: close,
  }
})

const navItems = computed<NavItem[]>(() => [
  {
    qa: 'analyses_lpu_cards_filters_sort_item',
    icon: 'ui-icon-down-up',
    title: sortLabel.value,
    subtitle: 'Сортировка',
    onClick: () => emit('open-sort-sheet'),
  },
  {
    qa: 'analyses_lpu_cards_filters_place_item',
    icon: 'ui-icon-place',
    title: placeTitle.value,
    subtitle: placeSubtitle.value,
    onClick: () => emit('open-place-drawer'),
  },
])

const toggleItems = computed<ToggleItem[]>(() => [
  {
    qa: 'analyses_lpu_cards_filters_online_order_item',
    icon: 'ui-icon-card',
    title: ANALYSES_LPU_ONLINE_ORDER_CHIP_LABEL,
    value: Boolean(props.draftFilters.onlineOrder),
    readonly: false,
    onInput: value => emit('update:draft-filters', { onlineOrder: value }),
  },
  {
    qa: 'analyses_lpu_cards_filters_discount_item',
    icon: 'ui-icon-percentage',
    title: ANALYSES_LPU_DISCOUNT_CHIP_LABEL,
    value: Boolean(props.draftFilters.withDiscount),
    readonly: false,
    onInput: value => emit('update:draft-filters', { withDiscount: value }),
  },
])

function close() {
  emit('input', false)
}
</script>
